import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Monitor, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const HINT_STORAGE_KEY = "freelanzen_install_hint_dismissed";

/**
 * Round icon-button matching the notification bell (w-10 h-10 rounded-full).
 *
 * Flow:
 *  - If the browser supports the PWA `beforeinstallprompt` event (Chrome/Edge
 *    on desktop + Android), we capture it and, on click, call `prompt()`.
 *  - If the app is already running as a standalone PWA (display-mode:standalone)
 *    we hide the button entirely — the user is already inside the app.
 *  - Otherwise (Safari desktop / Firefox) we open a small dialog explaining how
 *    to install it manually.
 *  - The first time the user lands on `/dashboard` and the install prompt is
 *    available, we surface a friendly bubble anchored to the button.
 */
export default function InstallAppButton() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)");
    const inStandalone = (mq && mq.matches) || window.navigator.standalone === true;
    setIsStandalone(!!inStandalone);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
      setHintOpen(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Show the hint bubble on the FIRST dashboard visit, once install is available.
  useEffect(() => {
    if (isStandalone) return;
    if (!location.pathname.startsWith("/dashboard")) return;
    let dismissed = null;
    try { dismissed = localStorage.getItem(HINT_STORAGE_KEY); } catch { /* ignore */ }
    if (dismissed) return;
    // Small delay so the layout has time to settle.
    const t = setTimeout(() => setHintOpen(true), 900);
    return () => clearTimeout(t);
  }, [location.pathname, isStandalone, deferredPrompt]);

  const dismissHint = () => {
    setHintOpen(false);
    try { localStorage.setItem(HINT_STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  if (isStandalone) return null;

  const onClick = async () => {
    dismissHint();
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch {
        /* user cancelled */
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }
    setHelpOpen(true);
  };

  return (
    <>
      <div className="relative">
        <button
          data-testid="install-app-btn"
          onClick={onClick}
          className="relative w-10 h-10 rounded-full bg-white border border-zen-border hover:bg-zen-subtle/20 flex items-center justify-center transition-colors"
          title="Scarica app desktop"
          aria-label="Scarica app desktop"
        >
          <Download size={17} strokeWidth={1.6}/>
        </button>

        {hintOpen && (
          <div
            data-testid="install-app-hint"
            className="absolute right-0 top-12 z-30 w-72 rounded-2xl bg-zen-surface border border-zen-border shadow-xl p-4 animate-fade-up"
          >
            {/* Arrow pointing to the button */}
            <div className="absolute -top-2 right-4 w-4 h-4 bg-zen-surface border-l border-t border-zen-border rotate-45"/>
            <button
              data-testid="install-app-hint-close"
              onClick={dismissHint}
              className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-zen-subtle/30 flex items-center justify-center text-zen-ink2"
              aria-label="Chiudi suggerimento"
            >
              <X size={14}/>
            </button>
            <div className="flex items-start gap-2 pr-6">
              <div className="w-8 h-8 rounded-full bg-zen-primary/15 flex items-center justify-center shrink-0">
                <Monitor size={16} className="text-zen-primary"/>
              </div>
              <div>
                <div className="font-heading text-sm text-zen-ink">
                  Installa FreelanZen sul desktop
                </div>
                <div className="text-xs text-zen-ink2 mt-1 leading-relaxed">
                  Aprilo con un click, senza browser. Un tap qui e sei a posto in 5 secondi.
                </div>
                <button
                  data-testid="install-app-hint-cta"
                  onClick={onClick}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-zen-primary hover:bg-zen-primaryHover text-white transition-colors"
                >
                  <Download size={12}/> Installa ora
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-md" data-testid="install-app-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Monitor size={18} className="text-zen-primary"/> Metti FreelanZen a portata di click
            </DialogTitle>
            <DialogDescription>
              Ecco come aggiungere FreelanZen al Dock o alla schermata Home in pochi secondi — così lo apri come qualsiasi altra app, senza passare dal browser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zen-ink">
            <div className="rounded-xl border border-zen-border p-3">
              <div className="font-medium mb-1">Sei su Chrome o Edge (Windows, Mac, Linux)</div>
              <div className="text-zen-ink2 text-xs">
                Apri il menu <b>⋮</b> in alto a destra e scegli <b>Installa FreelanZen…</b>
              </div>
            </div>
            <div className="rounded-xl border border-zen-border p-3">
              <div className="font-medium mb-1">Sei su Safari (Mac)</div>
              <div className="text-zen-ink2 text-xs">
                Dalla barra in alto: <b>File → Aggiungi al Dock…</b>
              </div>
            </div>
            <div className="rounded-xl border border-zen-border p-3">
              <div className="font-medium mb-1">Sei su iPhone o iPad</div>
              <div className="text-zen-ink2 text-xs">
                Tocca il pulsante <b>Condividi</b> (□↑) e poi <b>Aggiungi alla schermata Home</b>
              </div>
            </div>
            <div className="rounded-xl border border-zen-border p-3">
              <div className="font-medium mb-1">Sei su Android</div>
              <div className="text-zen-ink2 text-xs">
                Apri il menu <b>⋮</b> del browser e scegli <b>Installa app</b> o <b>Aggiungi alla schermata Home</b>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
